/**
 * Paged query atom — the effect-atom replacement for `useInfiniteQuery`.
 *
 * 语义（Atom.pull 实测，见 query-atom-paged.test.ts）：
 * - 挂载即拉第一页（Stream.paginate 从 firstPage 起，默认 1）
 * - 对 atom 写入（`useAtomSet` / `registry.set(atom, undefined)`）→ 拉下一页并
 *   **累积** 到 items（Atom.pull 默认 accumulation，等待期保留已加载页）
 * - hasNextPage 以最后已加载页的服务端 hasMore 为准（pull 的 done 标志落后一步）
 * - refresh / reactivityKeys 失效 / staleTime 过期重验 → 丢弃累积、从第一页重拉
 *
 * 失效语义与 queryAtomEffect 对齐：reactivityKeys 同屏刷新 + staleTime 跨页兜底。
 */

import { Cause, Effect, Option, Stream } from "effect";
import * as Atom from "effect/unstable/reactivity/Atom";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import type { QueryAtomOptions } from "./query-atom";

/** 一页数据的最小契约：服务端告知本页 items、是否还有下一页与总数 */
export type PagedResult<I> = {
  readonly items: ReadonlyArray<I>;
  readonly hasMore: boolean;
  readonly totalCount: number;
};

/** paged atom 的读取形状（对齐 useInfiniteQuery 的 data 语义） */
export type PagedState<P> = {
  /** 已加载的所有页（按拉取顺序；等待期保留已加载内容） */
  readonly pages: ReadonlyArray<P>;
  /** 所有页 items 的扁平化（等价原 pages.flatMap） */
  readonly items: ReadonlyArray<ItemsOf<P>>;
  /** 是否还有下一页（最后已加载页的服务端 hasMore） */
  readonly hasNextPage: boolean;
  /** 服务端总数（最后已加载页） */
  readonly totalCount: number;
  /** 正在拉取下一页（非首页等待） */
  readonly isFetchingNextPage: boolean;
};

/** 提取页类型 P 的 item 类型 */
export type ItemsOf<P> = P extends PagedResult<infer I> ? I : never;

type PullPayload<P> = {
  readonly done: boolean;
  readonly items: ReadonlyArray<P>;
};

/** queryAtomPaged 选项：queryAtom 选项（staleTime / reactivityKeys）+ 起始页 */
export type QueryAtomPagedOptions<K extends readonly unknown[]> = QueryAtomOptions<K> & {
  /** 起始页码，默认 1 */
  readonly firstPage?: number;
};

/**
 * Creates a memoized **paged** query-atom family from a page-fetching function.
 *
 * 返回 selector `(key, enabled?)`；读取得到 `AsyncResult<PagedState<P>, unknown>`，
 * 写入（`useAtomSet(atom)(undefined)`）拉下一页并累积。
 */
export function queryAtomPaged<P extends PagedResult<unknown>, E, R, K extends readonly unknown[]>(
  fetchPage: (key: K, page: number) => Effect.Effect<P, E, R>,
  options?: QueryAtomPagedOptions<K>,
): (
  key: K,
  enabled?: boolean,
) => Atom.Writable<AsyncResult.AsyncResult<PagedState<P>, E | Cause.NoSuchElementError>, void> {
  const firstPage = options?.firstPage ?? 1;

  const family = Atom.family((key: K) => {
    const pullAtom = Atom.pull<P, E>(() =>
      Stream.paginate(
        firstPage,
        (page: number): Effect.Effect<readonly [ReadonlyArray<P>, Option.Option<number>], E> =>
          Effect.map(
            fetchPage(key, page) as Effect.Effect<P, E>,
            (result): readonly [ReadonlyArray<P>, Option.Option<number>] => [
              [result],
              result.hasMore ? Option.some(page + 1) : Option.none(),
            ],
          ),
      ),
    );

    const stateAtom = pullAtom.pipe(
      Atom.mapResult((payload: PullPayload<P>): PagedState<P> => {
        const pages = payload?.items ?? [];
        const lastPage = pages.length > 0 ? pages[pages.length - 1] : undefined;
        return {
          pages,
          items: pages.flatMap((page) => page.items as ReadonlyArray<ItemsOf<P>>),
          hasNextPage: lastPage?.hasMore ?? false,
          totalCount: lastPage?.totalCount ?? 0,
          isFetchingNextPage: false,
        };
      }),
    );

    if (!options?.reactivityKeys) {
      return stateAtom;
    }
    return stateAtom.pipe(
      Atom.withReactivity(
        typeof options.reactivityKeys === "function"
          ? options.reactivityKeys(key)
          : options.reactivityKeys,
      ) as (a: typeof stateAtom) => typeof stateAtom,
    );
  });

  const disabled = Atom.make(AsyncResult.initial<PagedState<P>, E | Cause.NoSuchElementError>());
  return (key: K, enabled = true) =>
    enabled
      ? family(key)
      : (disabled as unknown as Atom.Writable<
          AsyncResult.AsyncResult<PagedState<P>, E | Cause.NoSuchElementError>,
          void
        >);
}
